/* RequestAccess — branded landing page rendered on a seller's vanity host
 * for ANONYMOUS visitors. Inverts the Shopify model: the storefront isn't
 * public; the visitor must request access from the seller, who decides
 * whether to invite them.
 *
 * Form submits to /api/public/request-access which:
 *   1. Stores a connection_requests row scoped to this seller.
 *   2. Pings the seller's in-app notifications.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useTenantHost } from "../context/TenantHostContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Lock, Send, CheckCircle2 } from "lucide-react";

export default function RequestAccess() {
  const { tenant } = useTenantHost();
  const [form, setForm] = useState({ email: "", company: "", note: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("Please enter a valid work email");
      return;
    }
    if (!tenant?.slug) {
      toast.error("This site is misconfigured. Try again later.");
      return;
    }
    setSending(true);
    try {
      await api.post("/public/request-access", {
        tenant_slug: tenant.slug,
        email: form.email.trim(),
        company: form.company.trim(),
        note: form.note.trim(),
      });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send request — try again");
    } finally {
      setSending(false);
    }
  };

  if (!tenant) return null;

  const primary = tenant.branding?.primary_color || "#0a1f1c";
  const accent  = tenant.branding?.accent_color || "#e8ff3a";

  return (
    <div className="min-h-screen flex flex-col" style={{ background: primary, color: "#fff" }} data-testid="request-access-page">
      {/* Header */}
      <header className="px-6 py-5 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-3">
          {tenant.branding?.logo_url ? (
            <img src={tenant.branding.logo_url} alt="" className="h-9 w-9 rounded-md object-contain bg-white/10 p-1"/>
          ) : (
            <div className="h-9 w-9 rounded-md grid place-items-center font-bold text-[15px]" style={{ background: accent, color: primary }}>
              {(tenant.name || "?").slice(0, 1)}
            </div>
          )}
          <div>
            <div className="font-semibold tracking-tight text-[15px]">{tenant.name}</div>
            {tenant.tagline && <div className="text-[12px] text-white/60 leading-tight">{tenant.tagline}</div>}
          </div>
        </div>
        <Link to="/login" className="text-[13px] text-white/70 hover:text-white" data-testid="request-access-existing-buyer-link">
          Already a buyer? Sign in →
        </Link>
      </header>

      {/* Body */}
      <main className="flex-1 grid lg:grid-cols-2 max-w-6xl w-full mx-auto px-6 py-12 gap-12 items-start">
        <section>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-white/10 text-white/80 border border-white/15">
            <Lock className="w-3 h-3"/> Closed network · invite-only
          </span>
          <h1 className="mt-6 text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
            Wholesale, the way<br/> {tenant.name} runs it.
          </h1>
          <p className="mt-5 text-[15px] text-white/70 leading-relaxed max-w-xl">
            This is {tenant.name}'s private buyer portal. Pricing, inventory, and order tools are reserved for
            approved retail partners. Tell us about your business and we'll be in touch.
          </p>
          <ul className="mt-7 space-y-3 text-[14px] text-white/85">
            {[
              "Approval typically within 1 business day",
              "Volume pricing + Net-30/60 once approved",
              "Live order tracking + insured shipping",
            ].map((b) => (
              <li key={b} className="flex items-start gap-2.5">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: accent }}/>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-2xl bg-white text-[#0b0d12] p-7" data-testid="request-access-form-card">
          {sent ? (
            <div className="text-center py-6" data-testid="request-access-sent-state">
              <div className="w-14 h-14 mx-auto rounded-full grid place-items-center" style={{ background: accent }}>
                <ShieldCheck className="w-6 h-6" style={{ color: primary }}/>
              </div>
              <h2 className="mt-5 text-2xl font-semibold tracking-tight">Request sent</h2>
              <p className="mt-2 text-[14px] text-foreground/65 max-w-sm mx-auto leading-relaxed">
                {tenant.name} will review your details and follow up by email. We'll send your invite link from there
                — keep an eye on <span className="font-medium">{form.email}</span>.
              </p>
              <button
                onClick={() => { setSent(false); setForm({ email: "", company: "", note: "" }); }}
                className="mt-7 text-[12.5px] text-foreground/60 hover:text-foreground"
                data-testid="request-access-send-another"
              >
                Send another request
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="request-access-form">
              <div>
                <h2 className="text-[20px] font-semibold tracking-tight">Request wholesale access</h2>
                <p className="text-[13px] text-foreground/60 mt-1">No browsing here — {tenant.name} approves every buyer one by one.</p>
              </div>
              <div>
                <Label htmlFor="ra-email" className="text-[12.5px]">Work email</Label>
                <Input
                  id="ra-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@yourcompany.com"
                  className="mt-1.5"
                  data-testid="request-access-email"
                />
              </div>
              <div>
                <Label htmlFor="ra-company" className="text-[12.5px]">Company name</Label>
                <Input
                  id="ra-company"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  placeholder="Apex Goods"
                  className="mt-1.5"
                  data-testid="request-access-company"
                />
              </div>
              <div>
                <Label htmlFor="ra-note" className="text-[12.5px]">A bit about your retail business <span className="text-foreground/45">(optional)</span></Label>
                <Textarea
                  id="ra-note"
                  rows={3}
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value.slice(0, 1000) })}
                  placeholder="3 stores in Brooklyn + online · monthly volume around $20K · interested in your hydrating serum line."
                  className="mt-1.5"
                  data-testid="request-access-note"
                />
              </div>
              <Button
                type="submit"
                disabled={sending}
                className="w-full rounded-full mt-2"
                style={{ background: primary, color: "#fff" }}
                data-testid="request-access-submit"
              >
                {sending ? "Sending…" : <><Send className="w-4 h-4 mr-2"/> Request access</>}
              </Button>
              <p className="text-[11px] text-foreground/55 text-center pt-1">
                Powered by <a href="https://verqo.com" target="_blank" rel="noreferrer" className="underline">verqo</a> · your data is sent only to {tenant.name}.
              </p>
            </form>
          )}
        </section>
      </main>
    </div>
  );
}
